import { RouteRecordRaw } from 'vue-router';
import { AppCoreEnums } from 'src/core/shared/enums';
import { ResolveParams, IRouteMeta } from 'src/core/models/routing';
import { AppPermissions } from 'src/shared/permissions';
import { ROUTE_PATH_OVERVIEW, ROUTE_PATH_PROPERTIES } from 'src/shared/constants';
import profilesPage from 'src/pages/profiles/profiles-page.vue';
import { useProfilesStore } from '@/stores/profiles/store';
import appTabsContainerPage from '@/core/pages/app-tabs-container-page.vue';

const ROUTE_PROFILES = 'profiles';
const ROUTE_PROFILES_DETAILS = 'profile-details';
const ROUTE_PROFILE_OVERVIEW = 'profile-overview';
const ROUTE_PROFILE_PROPERTIES = 'profile-properties';
const ROUTE_PROFILE_PERMISSIONS = 'profile-permissions';

const profilesRoutes: RouteRecordRaw = {
  name: ROUTE_PROFILES,
  path: ROUTE_PROFILES,
  meta: <IRouteMeta>{
    resolve: async (resolveParams: ResolveParams) =>
      Promise.all([useProfilesStore().loadProfiles(resolveParams), useProfilesStore().getPermissionTypes()]),
    containerType: AppCoreEnums.ContainerType.CollapsibleTableContainer,
    collapsibleTableTitleField: 'profileName',
    permissions: {
      readPermission: AppPermissions.ProfilesRead,
      writePermission: AppPermissions.ProfilesWrite,
      deletePermission: AppPermissions.ProfilesDelete,
      accessAll: AppPermissions.AccessAll,
    },
  },
  component: () => profilesPage,
  children: [
    {
      name: ROUTE_PROFILES_DETAILS,
      path: ':id',
      component: () => appTabsContainerPage,
      meta: <IRouteMeta>{
        containerType: AppCoreEnums.ContainerType.TabsContainer,
        resolve: async (resolveParams: ResolveParams) => {
          const { useProfilesStore } = await import('@/stores/profiles/store');
          return useProfilesStore().loadProfileById(resolveParams);
        },
        tabs: [
          {
            label: 'core.overview',
            to: ROUTE_PROFILE_OVERVIEW,
            icon: 'hugeicons:view',
          },
          {
            label: 'core.properties',
            to: ROUTE_PROFILE_PROPERTIES,
            icon: 'hugeicons:pencil-edit-02',
          },
          {
            label: 'profiles.permissions',
            to: ROUTE_PROFILE_PERMISSIONS,
            icon: 'hugeicons:security',
          },
        ],
      },
      redirect: { name: ROUTE_PROFILE_OVERVIEW },
      children: [
        {
          name: ROUTE_PROFILE_OVERVIEW,
          path: ROUTE_PATH_OVERVIEW,
          component: () => import('@/core/pages/app-form-container-page.vue'),
          meta: <IRouteMeta>{
            containerType: AppCoreEnums.ContainerType.FormsContainer,
            formComponent: () => import('@/forms/profiles/profile-overview.vue'),
            formModel: () => useProfilesStore().get('profileOverview'),
            resolve: async (resolveParams: ResolveParams) => {
              if (!resolveParams.useStoreData) return Promise.resolve();
              const { useProfilesStore } = await import('@/stores/profiles/store');
              return useProfilesStore().loadProfileById(resolveParams);
            },
            permissions: {
              readPermission: AppPermissions.ProfilesRead,
            },
          },
        },
        {
          name: ROUTE_PROFILE_PROPERTIES,
          path: ROUTE_PATH_PROPERTIES,
          component: () => import('@/core/pages/app-form-container-page.vue'),
          meta: <IRouteMeta>{
            containerType: AppCoreEnums.ContainerType.FormsContainer,
            formComponent: () => import('@/forms/profiles/profile-properties.vue'),
            formModel: () => useProfilesStore().get('profileProperties'),
            formSave: () => useProfilesStore().saveProfile,
            resolve: async (resolveParams: ResolveParams) => {
              if (!resolveParams.useStoreData) return Promise.resolve();
              const { useProfilesStore } = await import('@/stores/profiles/store');
              return useProfilesStore().loadProfileById(resolveParams);
            },
            permissions: {
              readPermission: AppPermissions.ProfilesRead,
              writePermission: AppPermissions.ProfilesWrite,
            },
          },
        },
        {
          name: ROUTE_PROFILE_PERMISSIONS,
          path: 'permissions',
          component: () => import('@/pages/profiles/profile-permissions-page.vue'),
          meta: <IRouteMeta>{
            containerType: AppCoreEnums.ContainerType.TableContainer,
            resolve: async (resolveParams: ResolveParams) => {
              const { useProfilesStore } = await import('@/stores/profiles/store');
              return useProfilesStore().loadProfilePermissions(resolveParams);
            },
            permissions: {
              writePermission: AppPermissions.ProfilePermissionsAssign,
            },
          },
        },
      ],
    },
  ],
};

export {
  profilesRoutes,
  ROUTE_PROFILES,
  ROUTE_PROFILES_DETAILS,
  ROUTE_PROFILE_OVERVIEW,
  ROUTE_PROFILE_PROPERTIES,
  ROUTE_PROFILE_PERMISSIONS,
};
